<template>
  <input
    v-model="code"
    :disabled="disabled"
    ref="otpInput"
    id="otpInput"
    class="e-verification-code-input"
    :class="{'e-verification-code-input--filled' : filled, 'e-verification-code-input--disabled' : disabled}"
    type="text"
    pattern="[0-9A-Za-z]"
    :inputmode="getInputMode()"
    :autocomplete="mobile ? 'one-time-code' : 'off'"
    maxlength="6"
    @input="handleInput"
    @change="handleChange"
    @paste="onPaste" />
</template>

<script setup>
import { ref, watch, onMounted, nextTick } from 'vue';
import { useDisplay } from 'vuetify';

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: String,
    default: '',
  },
  allowedChars: {
    type: RegExp,
    default: /\D/g,
  },
  inputMode: {
    type: String,
    default: 'numeric',
  },
})
const emit = defineEmits(['filled', 'update:model-value'])
let code = ref('')
const otpInput = ref(null)
const { mobile } = useDisplay()

const focusInput = () => {
  if (otpInput.value) {
    otpInput.value.focus();
  }
};

watch(
  () => props.modelValue,
  () => {
    code.value = props.modelValue
  },
  { immediate: true }
)

let filled = ref(false)

watch(
  () => code.value,
  async () => {
    if (code.value.length === 6) {
      filled.value = true
      // Emit the code when fully filled
      emit('update:model-value', code.value)
      emit('filled', code.value)
      otpInput.value?.blur() // Blur the input when filled
    } else {
      if(otpInput.value){
        await nextTick()
        focusInput()
      }
      filled.value = false
    }
  }
)

onMounted(() => {
  focusInput()
})

const handleInput = (event) => {
  code.value = event.target.value.replace(props.allowedChars, '')
}

const handleChange = () => {
  // Trigger submit when autofill happens and the change event is fired
  if (code.value.length === 6) {
    otpInput.value?.blur() // Blur input when filled
  }
}

const onPaste = (event) => {
	const pastedText = (event.clipboardData || window.clipboardData).getData('text');
	if (props.allowedChars.test(pastedText)) {
		event.preventDefault()
	}
}

const getInputMode = () => {
  // Set inputmode to "numeric" to provide a numerical keyboard on mobile devices
  return props.inputMode === 'numeric' ? 'numeric' : 'text'
}
</script>

<style lang="scss" scoped>
@import '../../scss/styles';

.e-verification-code-input {
  border: 1px solid $grayscale300;
  border-radius: 6px;
  text-align: center;
  height: 44px;
  outline: none;
  padding: 0 18px;
  font-size: 18px;
  letter-spacing: 18px;
  max-width: 200px;
  width: 100%;
  &:focus-visible {
    border: 1px solid $primary300;
    @include blue-ring;
    + input {
      border-inline-start: none;
    }
  }
  &--filled {
    padding-right: 0!important;
  }
  &--disabled {
    border-color: $grayscale200;
    background-color: $grayscale50;
  }

  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type='number'] {
    -moz-appearance: textfield;
  }
}

.v-theme--dark {
  .e-verification-code-input {
    border-color: $grayscale700;
    &:focus-visible {
      border-color: $primary300;
    }
    &:hover:not(:focus-visible):not(.e-verification-code-input--disabled) {
      border-color: $grayscale500;
    }
    &--disabled {
      background-color: transparent;
    }
  }
}
</style>
